/**
 * publicPayload — o sinal de que ESTA página foi servida pelo servidor com
 * dado público embutido.
 *
 * ## Por que existe
 *
 * A abertura de SEO orgânico tem duas metades que não sobem no mesmo deploy: o
 * client (hosting) e a Cloud Function que serve `/livro/**` com o dado embutido
 * (functions + rewrite). As `firestore.rules` continuam FECHADAS por decisão —
 * o visitante anônimo não lê o catálogo direto, ele recebe o dado já pronto.
 *
 * Sem este sinal, hosting-novo + function-velha seria uma REGRESSÃO real e
 * silenciosa: a rota deixaria de mandar o visitante pro `/login` (o
 * comportamento honesto de hoje) e passaria a renderizar "livro não
 * encontrado", porque a leitura do catálogo é negada pelas rules. Trocaríamos
 * um portão por uma mentira.
 *
 * Com ele, a ordem de deploy deixa de importar: sem payload o app se comporta
 * exatamente como antes (rota gated), e o modo público liga sozinho no instante
 * em que a function entra no ar. É a mesma ideia de "não confie na ordem,
 * derive do que está presente" que o projeto já aprendeu com o Zod da leva de
 * waitlist e com o `.strict()` da leva de creators.
 *
 * ## Formato
 *
 * O servidor injeta UM bloco de dado no `<body>`:
 *
 *   <script type="application/json" id="__LIVRA_PUBLIC__">
 *     {"kind":"book","data":{…}}
 *   </script>
 *
 * `type="application/json"` não é executável, e é por isso que passa pela CSP
 * do Hosting (`script-src 'self'`, sem `unsafe-inline`) — mesma razão pela qual
 * o JSON-LD sempre funcionou. Um `<script>` de verdade seria bloqueado.
 *
 * O envelope tem `kind` pra que author/list/profile possam entrar depois sem um
 * segundo elemento e um segundo leitor — dois catálogos do mesmo dado é como o
 * BUG-81 e o BUG-87 nasceram.
 */

export const PUBLIC_PAYLOAD_ELEMENT_ID = "__LIVRA_PUBLIC__";

/**
 * `book` = ficha (`renderBookPage`, 06/08). `livros` = uma página do hub
 * `/livros` (mesma function, 14/09) — o consumidor é `LivrosPage.tsx`.
 */
export type PublicPayloadKind = "book" | "livros";
const KNOWN_KINDS: ReadonlySet<string> = new Set<PublicPayloadKind>(["book", "livros"]);

export interface PublicPayloadEnvelope<T = unknown> {
  kind: PublicPayloadKind;
  data: T;
}

/**
 * Memo de módulo: o bloco é estático (o servidor o escreve uma vez), então
 * parsear a cada render seria desperdício. `undefined` = ainda não olhei;
 * `null` = olhei e não tem.
 */
let cached: PublicPayloadEnvelope | null | undefined;

function parse(): PublicPayloadEnvelope | null {
  if (typeof document === "undefined") return null;
  const el = document.getElementById(PUBLIC_PAYLOAD_ELEMENT_ID);
  if (!el?.textContent) return null;
  try {
    const raw: unknown = JSON.parse(el.textContent);
    if (!raw || typeof raw !== "object") return null;
    const envelope = raw as Partial<PublicPayloadEnvelope>;
    // `kind` ausente ou desconhecido é payload de uma versão que não
    // conhecemos: ignora em vez de assumir "book". Ignorar degrada pro modo
    // gated (seguro); assumir renderizaria a tela errada com dado de outra forma.
    if (typeof envelope.kind !== "string" || !KNOWN_KINDS.has(envelope.kind)) return null;
    if (!envelope.data || typeof envelope.data !== "object") return null;
    return { kind: envelope.kind as PublicPayloadKind, data: envelope.data };
  } catch {
    return null;
  }
}

/** Envelope público desta página, ou `null` se a página não foi pré-renderizada. */
export function readPublicPayload(): PublicPayloadEnvelope | null {
  if (cached === undefined) cached = parse();
  return cached;
}

/** `true` quando o servidor embutiu dado público — o que habilita o modo anônimo. */
export function hasPublicPayload(kind?: PublicPayloadKind): boolean {
  const envelope = readPublicPayload();
  if (!envelope) return false;
  return kind ? envelope.kind === kind : true;
}

/** Só para teste: zera o memo de módulo. */
export function resetPublicPayloadCache(): void {
  cached = undefined;
}
